import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Container,
  CardMedia,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Public as PublicIcon,
  AccountBalance as LocalChapterIcon,
  Link as LinkIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { getNewsfeedById } from '../../endpoints/newsfeedTs';
import { useApiGet } from '../../utils/httpClientTs';
import { NewsFeedDetail } from '../../types/newsfeed';
import dayjs from 'dayjs';

export default function NewsFeedView(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();

  // Fetch newsfeed detail from API
  const {
    data: newsfeed,
    isLoading,
    error,
  } = useApiGet<NewsFeedDetail>(
    ['newsfeed-detail', id],
    () => getNewsfeedById(id!),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: newsfeed?.title || 'PEO Newsfeed',
          text: newsfeed?.description || '',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show success toast
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMMM D, YYYY');
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  // Sort sections and content by order
  const sortedSections =
    newsfeed?.sections
      ?.sort((a, b) => a.order - b.order)
      .map((section) => ({
        ...section,
        content: section.content.sort((a, b) => a.order - b.order),
      })) || [];

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !newsfeed) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Newsfeed not found or error loading content. Please check the URL and
          try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ mb: 4, overflow: 'hidden', borderRadius: 3 }}>
        {newsfeed.image_url && (
          <CardMedia
            component="img"
            sx={{
              maxHeight: '400px',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto',
            }}
            image={newsfeed.image_url}
            alt={newsfeed.title}
          />
        )}
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} lg={9}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                {newsfeed.title}
              </Typography>
              {newsfeed.description && (
                <Typography
                  variant="h6"
                  color="text.secondary"
                  paragraph
                  sx={{ lineHeight: 1.6 }}
                >
                  {newsfeed.description}
                </Typography>
              )}

              {/* Meta Information */}
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
                <Chip
                  icon={<CalendarIcon />}
                  label={`${formatDate(newsfeed.start_date)} - ${formatDate(newsfeed.end_date)}`}
                />
                <Chip
                  icon={<PublicIcon />}
                  label={getStatusText(newsfeed.is_active)}
                  color={getStatusColor(newsfeed.is_active)}
                  variant="outlined"
                />
                {newsfeed.is_international && (
                  <Chip
                    icon={<PublicIcon />}
                    label="International"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {newsfeed.local_chapter_id && (
                  <Chip
                    icon={<LocalChapterIcon />}
                    label="Local Chapter"
                    color="secondary"
                    variant="outlined"
                  />
                )}
                {newsfeed.spd_chapter_id && (
                  <Chip
                    icon={<LocationIcon />}
                    label="SPD Chapter"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} lg={3}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Share">
                    <IconButton
                      onClick={handleShare}
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {newsfeed.link_url && (
                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    href={newsfeed.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    size="large"
                  >
                    Learn More
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      {/* Content Sections */}
      {sortedSections.length > 0 && (
        <Box sx={{ mb: 4 }}>
          {sortedSections.map((section, sectionIndex) => (
            <Paper
              key={section.id || sectionIndex}
              elevation={0}
              sx={{ mb: 3, borderRadius: 3 }}
            >
              <CardContent sx={{ p: 4 }}>
                {section.title && (
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 3 }}
                  >
                    {section.title}
                  </Typography>
                )}

                <Stack spacing={3}>
                  {section.content.map((content, contentIndex) => (
                    <Box key={content.id || contentIndex}>
                      {/* Paragraph Content */}
                      {content.content_type_id ===
                        '60308536096c4ce088bee75af7ae8ae2' &&
                        content.paragraph && (
                          <Typography
                            variant="body1"
                            paragraph
                            sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}
                          >
                            {content.paragraph}
                          </Typography>
                        )}

                      {/* List Content */}
                      {content.content_type_id ===
                        'c1e257bc8c674ae28a7b2085518aea9e' &&
                        content.list &&
                        content.title && (
                          <Box>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{ fontWeight: 600, mb: 2 }}
                            >
                              {content.title}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                              {content.list.map((item, itemIndex) => (
                                <Typography
                                  key={item.id || itemIndex}
                                  component="li"
                                  variant="body1"
                                  sx={{
                                    lineHeight: 1.8,
                                    fontSize: '1.1rem',
                                    mb: 1,
                                    '&:last-child': { mb: 0 },
                                  }}
                                >
                                  {item.is_link && item.link_url ? (
                                    <Button
                                      component="a"
                                      href={item.link_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      startIcon={<LinkIcon />}
                                      sx={{
                                        textTransform: 'none',
                                        p: 0,
                                        minWidth: 'auto',
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                        '&:hover': {
                                          textDecoration: 'none',
                                        },
                                      }}
                                    >
                                      {item.content}
                                    </Button>
                                  ) : (
                                    item.content
                                  )}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}

                      {/* Video Content */}
                      {content.content_type_id ===
                        'fd9dd86a20e24e79b920e191f3b8203a' &&
                        content.video_url && (
                          <Box>
                            {content.title && (
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: 600, mb: 2 }}
                              >
                                {content.title}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: '600px',
                                margin: '0 auto',
                              }}
                            >
                              <video
                                controls
                                style={{ width: '100%', height: 'auto' }}
                                src={content.video_url}
                              >
                                Your browser does not support the video tag.
                              </video>
                            </Box>
                          </Box>
                        )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Paper>
          ))}
        </Box>
      )}

      {/* Footer Actions */}
      {newsfeed.link_url && (
        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}
        >
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            href={newsfeed.link_url}
            target="_blank"
            rel="noopener noreferrer"
            size="large"
          >
            Learn More
          </Button>
        </Paper>
      )}
    </Box>
  );
}
